<template>
  <!--
    The only design-system entry point for Nuxt UI's Select (atomic-boundaries).
    Owns the vendor's own quirks; Mealz's Select API belongs to Select.atom.vue.
  -->
  <USelect
    v-bind="vendorProps"
    :items="vendorOptions"
    :placeholder="placeholder"
    :disabled="disabled"
    :loading="loading"
    :required="required"
    :multiple="multiple"
    :size="size"
    variant="none"
    :ui="selectUi"
    @update:model-value="handleUpdate"
  >
    <template
      v-if="$slots['default']"
      #default
    >
      <slot />
    </template>
  </USelect>
</template>

<script setup lang="ts" generic="TValue extends number | string | (number | string)[]">
import { computed } from 'vue'

import USelect from '@nuxt/ui/components/Select.vue'

import { ALL_OPTION_VALUE } from '~/utils/allOptionValue'

import { fieldControlUi } from './fieldControlUi'
import { omitEmptyStringProps } from './omitEmptyStringProps'

/** Control sizes USelect draws. */
export type VendorSelectSize = 'lg' | 'md' | 'sm' | 'xl' | 'xs'

/** The leading avatar USelect draws for an option; omitted `src` draws its placeholder. */
export interface VendorSelectOptionAvatar {
  src?: string
  alt: string
}

/** One selectable choice, in the key shape USelect reads by default. */
export interface VendorSelectOption {
  value: number | string
  label: string
  description?: string
  avatar?: VendorSelectOptionAvatar
  disabled?: boolean
}

/** A non-selectable heading. USelect renders `type: 'label'` as a group label. */
export interface VendorSelectGroupLabel {
  type: 'label'
  label: string
  /** Unsettable: a heading carries no value. Present so the union keeps the
   *  field and USelect's generic still infers the model type from it. */
  value?: never
}

/** What the options list may hold: choices, optionally divided by headings. */
export type VendorSelectItem = VendorSelectGroupLabel | VendorSelectOption

const props = withDefaults(
  defineProps<{
    /** Selected value; an array selects several, matching `multiple`. Empty means nothing selected. */
    modelValue: TValue
    /** The choices, in render order, optionally divided by headings. */
    options: VendorSelectItem[]
    /** Shown while nothing is selected. Already translated. */
    placeholder?: string
    /** Id for the trigger. Empty lets the surrounding field supply one. */
    id?: string
    /** Prevents interaction. */
    disabled?: boolean
    /** Shows the vendor's loading affordance in the trigger. */
    loading?: boolean
    /** Marks the control as mandatory. */
    required?: boolean
    /** Non-empty prepends the one fixed "All" choice, whose value is ALL_OPTION_VALUE; the options follow it. */
    allLabel?: string
    /** Paints the error outline and announces the trigger as invalid. */
    invalid?: boolean
    /** Selects several values instead of one. Pair with an array model. */
    multiple?: boolean
    /** Accessible name for the trigger, kept apart from the chosen value. Empty leaves the visible text as the name. */
    ariaLabel?: string
    /** Control height; match the neighbouring inputs. */
    size?: VendorSelectSize
  }>(),
  {
    allLabel: '',
    ariaLabel: '',
    disabled: false,
    id: '',
    invalid: false,
    loading: false,
    multiple: false,
    placeholder: '',
    required: false,
    size: 'md'
  }
)

const emit = defineEmits<{ 'update:modelValue': [value: TValue] }>()

defineSlots<{
  /** Replaces the text the trigger displays for the current value. */
  default?: () => unknown
}>()

const EMPTY_OPTION_TOKEN = '__mealz_empty_select_option__'
const ALL_COLLISION_TOKEN = '__mealz_all_select_option__'

const isVendorSelectOption = (option: VendorSelectItem): option is VendorSelectOption =>
  !('type' in option && option.type === 'label')

const hasOptionValue = (value: number | string): boolean =>
  props.options.some((option) => isVendorSelectOption(option) && option.value === value)

const hasEmptyOption = computed(() => hasOptionValue(''))

const hasAllCollision = computed(() => props.allLabel !== '' && hasOptionValue(ALL_OPTION_VALUE))

// Tokens no option, the fixed All value, or each other already use, so a real value is never mistaken for one.
const internalTokens = computed(() => {
  const taken = new Set<number | string>([
    ALL_OPTION_VALUE,
    ...props.options.filter(isVendorSelectOption).map((option) => option.value)
  ])
  const claim = (base: string): string => {
    let token = base
    while (taken.has(token)) token += '_'
    taken.add(token)
    return token
  }
  return { allCollision: claim(ALL_COLLISION_TOKEN), empty: claim(EMPTY_OPTION_TOKEN) }
})

const emptyOptionValue = computed(() => internalTokens.value.empty)
const allCollisionValue = computed(() => internalTokens.value.allCollision)

const choices = computed(() =>
  props.allLabel === ''
    ? props.options
    : [{ label: props.allLabel, value: ALL_OPTION_VALUE }, ...props.options]
)

const vendorOptions = computed(() =>
  choices.value.map((option, index) => {
    if (!isVendorSelectOption(option)) return option
    if (option.value === '') return { ...option, value: emptyOptionValue.value }
    const isRealAll = hasAllCollision.value && index > 0 && option.value === ALL_OPTION_VALUE
    return isRealAll ? { ...option, value: allCollisionValue.value } : option
  })
)

const vendorModelValue = computed(() => {
  if (!hasEmptyOption.value) {
    return props.modelValue
  }

  if (Array.isArray(props.modelValue)) {
    return props.modelValue.map((value) => (value === '' ? emptyOptionValue.value : value))
  }

  return props.modelValue === '' ? emptyOptionValue.value : props.modelValue
})

// `aria-invalid` rides the same v-bind spread since USelect's typed props don't declare it.
const vendorProps = computed(() => ({
  ...omitEmptyStringProps({ id: props.id, modelValue: vendorModelValue.value }),
  'aria-invalid': props.invalid ? true : undefined,
  'aria-label': props.ariaLabel === '' ? undefined : props.ariaLabel
}))

// The vendor trigger is driven with `variant="none"`, so the shared skin owns
// the whole paint; only the outline colour changes with the field verdict.
const selectUi = computed(() => ({
  base: [
    fieldControlUi.select.base,
    props.invalid ? fieldControlUi.select.outlineInvalid : fieldControlUi.select.outline
  ],
  placeholder: fieldControlUi.select.placeholder,
  trailingIcon: fieldControlUi.select.trailingIcon
}))

const isSelectableValue = (value: unknown): value is number | string =>
  typeof value === 'string' || typeof value === 'number'

const isSelectableValues = (value: unknown): value is (number | string)[] =>
  Array.isArray(value) && value.every(isSelectableValue)

/** USelect's own generics don't carry TValue; narrows its emitted string/number(s) to it once, here. */
const isModelValue = (value: number | string | (number | string)[]): value is TValue =>
  isSelectableValue(value) || isSelectableValues(value)

const getModelValue = (value: number | string): number | string => {
  if (value === emptyOptionValue.value) return ''
  return value === allCollisionValue.value ? ALL_OPTION_VALUE : value
}

const emitModelValue = (value: number | string | (number | string)[]) => {
  if (isModelValue(value)) {
    emit('update:modelValue', value)
    return
  }

  throw new Error('VendorSelect received a selection shape that conflicts with its model')
}

const handleUpdate = (value: unknown) => {
  if (isSelectableValues(value)) {
    emitModelValue(value.map(getModelValue))
    return
  }

  if (isSelectableValue(value)) {
    emitModelValue(getModelValue(value))
    return
  }

  throw new Error('VendorSelect received a value USelect cannot have taken from its options')
}
</script>
